import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import React, { useEffect, useState } from "react";
import ListItemText from "@mui/material/ListItemText";
import { useSelector, useDispatch } from "react-redux";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getRequest, postRequest, showMessage } from "../../../../config.js";
import { setCompanies } from "../../../../reducers/companiesReducer.js";

export default function TempPermissionsClient({ profileClass, advisors, setRefresh, company }) {
    const user = useSelector(state => state.user);
    const companies = useSelector(state => state.companies);
    const dispatch = useDispatch();

    const [allCompanies, setAllCompanies] = useState([]);
    const [tempModules, setTempModules] = useState([]);
    // Flat list of all TempSections, each enriched with .permission map (mirrors PermissionsClient)
    const [sections, setSections] = useState([]);
    const [openAccordions, setOpenAccordions] = useState({});

    // ─── Flatten companies (parent + children) — identical to PermissionsClient ───
    useEffect(() => {
        if (!companies?.length) return;
        const seen = new Set();
        const flat = [];
        for (const comp of companies) {
            const copy = { ...comp };
            delete copy.childrenCompanies;
            if (!seen.has(comp._id)) { flat.push(copy); seen.add(comp._id); }
            if (Array.isArray(comp.childrenCompanies)) {
                for (const child of comp.childrenCompanies) {
                    const childCopy = { ...child };
                    delete childCopy.childrenCompanies;
                    if (!seen.has(child._id)) { flat.push(childCopy); seen.add(child._id); }
                }
            }
        }
        setAllCompanies(flat);
    }, [companies]);

    // ─── Fetch all TempModules with their TempSections ────────────────────────────
    useEffect(() => {
        getRequest('/modules/all-modules-section', 'not-showMessage').then(res => {
            if (res?.code === 200 && Array.isArray(res.body)) {
                setTempModules(res.body.filter(m => m.sections?.length > 0));
            }
        });
    }, []);

    // ─── Build sections with permission data (mirrors PermissionsClient logic) ────
    // Each section gets: { sectionNo, sectionName, moduleId, moduleName, moduleKey,
    //   permission: { [companyId]: { [advisorId]: advisorDoc } } }
    useEffect(() => {
        if (!tempModules.length || !allCompanies.length) return;

        // Flatten all TempSections annotated with their parent module info
        const flatSections = tempModules.flatMap(m =>
            m.sections.map(s => ({
                ...s,
                moduleId: m._id,
                moduleName: m.moduleName,
                moduleKey: m.moduleKey,
            }))
        );

        const sectionObj = JSON.parse(JSON.stringify(flatSections));
        const userType = user.role === 'Advisor' ? 'advisors' : 'investors';

        for (const comp of allCompanies) {
            if (!comp[userType]) continue;
            for (const advisorId of Object.keys(comp[userType])) {
                const advisorSettings = comp[userType][advisorId];
                const advisor = advisors.find(({ _id }) => _id == advisorId);
                if (!advisorSettings?.modules || !advisor) continue;

                for (const [key, value] of Object.entries(advisorSettings.modules)) {
                    if (!value) continue;
                    const idx = sectionObj.findIndex(s => s.sectionNo === key);
                    if (idx >= 0) {
                        if (!sectionObj[idx].permission) sectionObj[idx].permission = {};
                        if (!sectionObj[idx].permission[comp._id]) sectionObj[idx].permission[comp._id] = {};
                        sectionObj[idx].permission[comp._id][advisorId] = advisor;
                    }
                }
            }
        }
        setSections(sectionObj);
    }, [allCompanies, tempModules]);

    const toggleAccordion = (moduleId) => {
        setOpenAccordions(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
    };

    // ─── Single-section permission toggle (mirrors PermissionsClient.handlePermissions) ──
    const handlePermissions = async (e, advisor, sectionNo, comp) => {
        const status = e.target.checked;
        const res = await postRequest(`/user/advisorPermissions/${comp._id}`, {
            advisor,
            company: comp,
            advisorId: advisor._id,
            sectionNo,
            status,
            firmId: user.companyId._id,
        });

        const userType = advisor.role === 'Advisor' ? 'advisors' : 'investors';

        if (res?.code === 200) {
            const sectionObj = JSON.parse(JSON.stringify(sections));
            const sectionIndex = sectionObj.findIndex(s => s.sectionNo === sectionNo);
            const companyId = comp._id;
            const advisorId = advisor._id;

            if (status) {
                if (!sectionObj[sectionIndex].permission || !sectionObj[sectionIndex].permission[companyId]) {
                    sectionObj[sectionIndex] = {
                        ...sectionObj[sectionIndex],
                        permission: { [companyId]: null },
                    };
                }
                sectionObj[sectionIndex].permission[companyId] = {
                    ...sectionObj[sectionIndex].permission[companyId],
                    [advisorId]: {
                        ...advisor,
                        permissions: {
                            ...(comp[userType]?.[advisorId]?.modules),
                            [sectionNo]: status,
                        },
                    },
                };
            } else {
                delete sectionObj[sectionIndex].permission?.[companyId]?.[advisorId];
            }

            const updatedData = allCompanies.map(item => {
                if (item._id === companyId) {
                    return {
                        ...item,
                        [userType]: {
                            ...item[userType],
                            [advisorId]: {
                                ...item[userType]?.[advisorId],
                                modules: {
                                    ...item[userType]?.[advisorId]?.modules,
                                    [sectionNo]: status,
                                },
                            },
                        },
                    };
                }
                return item;
            });
            dispatch(setCompanies(updatedData));
            setSections(sectionObj);
            showMessage(res.message);
            setRefresh(r => !r);
        }
    };

    // ─── Bulk-toggle all temp sections for an advisor on a company ────────────────
    const handleMultiPermissions = async (e, advisor, sectionNos, comp) => {
        const _checked = e.target.checked;
        const result = await postRequest(`/user/advisorMultiPermissions/${comp._id}`, {
            advisor,
            company: comp,
            advisorId: advisor._id,
            sectionNo: sectionNos,
            status: _checked,
            firmId: user.companyId._id,
        });

        if (result?.code === 200) {
            const sectionObj = JSON.parse(JSON.stringify(sections));
            sectionObj.forEach((section, sectionIndex) => {
                const advisorId = advisor._id;
                if (_checked) {
                    if (sectionObj[sectionIndex].permission?.[comp._id]) {
                        if (!sectionObj[sectionIndex].permission[comp._id][advisorId]) {
                            sectionObj[sectionIndex].permission[comp._id] = {
                                ...sectionObj[sectionIndex].permission[comp._id],
                                [advisorId]: advisor,
                            };
                        }
                    } else {
                        sectionObj[sectionIndex] = {
                            ...sectionObj[sectionIndex],
                            permission: {
                                ...sectionObj[sectionIndex].permission,
                                [comp._id]: { [advisorId]: advisor },
                            },
                        };
                    }
                } else {
                    delete sectionObj[sectionIndex].permission?.[comp._id]?.[advisorId];
                }
            });
            setSections(sectionObj);

            // Keep allCompanies in Redux in sync so that subsequent single-section
            // permission changes don't overwrite this bulk update with stale data.
            const userType = advisor.role === 'Advisor' ? 'advisors' : 'investors';
            const currentModules = allCompanies
                .find(c => c._id === comp._id)?.[userType]?.[advisor._id]?.modules || {};
            const newModules = { ...currentModules };
            sectionNos.forEach(sno => { newModules[sno] = _checked; });
            const updatedCompanies = allCompanies.map(item => {
                if (item._id === comp._id) {
                    return {
                        ...item,
                        [userType]: {
                            ...item[userType],
                            [advisor._id]: {
                                ...item[userType]?.[advisor._id],
                                modules: newModules,
                            },
                        },
                    };
                }
                return item;
            });
            dispatch(setCompanies(updatedCompanies));
        }
        showMessage(result.message);
        setRefresh(r => !r);
    };

    // Returns true if an advisor has all temp sections granted for a given company
    const checkAdvisorHasAllAllocations = ({ advisor, company: comp }) => {
        const userType = user.role === 'Advisor' ? 'advisors' : 'investors';
        if (advisor && comp?.[userType]?.[advisor._id]?.modules) {
            const allTempSectionNos = sections.map(s => s.sectionNo);
            if (!allTempSectionNos.length) return false;
            const advisorPermissions = Object.entries(comp[userType][advisor._id].modules)
                .filter(([, val]) => val === true)
                .map(([key]) => key);
            return allTempSectionNos.every(no => advisorPermissions.includes(no));
        }
        return false;
    };

    // ─── Empty states ─────────────────────────────────────────────────────────────
    if (!allCompanies.length) {
        return (
            <div className="df jcc c15 fs1-75rem fw500 lh2 mt2rem">
                No clients added for permissions
            </div>
        );
    }

    if (!tempModules.length) {
        return (
            <div className="df jcc c15 fs1-25rem fw500 lh2 mt2rem">
                No modules available
            </div>
        );
    }

    // Group the flat sections list back into per-module buckets for accordion rendering,
    // preserving the order returned by the API
    const sectionsByModule = tempModules
        .map(module => ({
            module,
            moduleSections: sections.filter(s => String(s.moduleId) === String(module._id)),
        }))
        .filter(({ moduleSections }) => moduleSections.length > 0);

    const allTempSectionNos = sections.map(s => s.sectionNo);

    return (
        <div className="sectionWrapper p1rem fs0-75rem pr mr2rem ml1rem pr">
            <div className="table-permission">
                <table id="table-temp-permissions" className="table-userPermissions table-clientPermissions">

                    {/* ── Header: company names + bulk-select dropdown (same as PermissionsClient) ── */}
                    <thead className="ps t0">
                        <tr>
                            <th className="index-table"></th>
                            {allCompanies.map((comp, cIndex) => (
                                <th className="user-area" key={`temp-comp-${cIndex}`}>
                                    <div className="df aic">
                                        <div className="mr1rem wfull pr">
                                            <span className="di fs1rem c0">{comp.cName}</span>
                                            {comp.subscription ? (
                                                <span className="c7 di ml0-5rem">
                                                    ({comp.subscription?.toUpperCase()})
                                                </span>
                                            ) : (
                                                <span className="c8 di ml0-5rem">(INACTIVE)</span>
                                            )}
                                            {/* Bulk-allocate all temp sections for an advisor */}
                                            <label className="">
                                                <Select
                                                    multiple
                                                    className="select-advisor w10 pa"
                                                    id={`bulk-select-${cIndex}`}
                                                    sx={{ "& .MuiSvgIcon-root": { color: "#4A67FF" } }}
                                                    IconComponent={ExpandMoreIcon}
                                                    value={[]}
                                                    MenuProps={{
                                                        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
                                                        transformOrigin: { vertical: 'top', horizontal: 'right' },
                                                    }}
                                                >
                                                    {advisors.map((advisor, idx) => (
                                                        <MenuItem
                                                            key={`${cIndex}-bulk-${idx}`}
                                                            value={advisor}
                                                            className="select-advisor-list ttc"
                                                        >
                                                            <Checkbox
                                                                checked={checkAdvisorHasAllAllocations({ advisor, company: comp })}
                                                                onClick={(e) =>
                                                                    handleMultiPermissions(e, advisor, allTempSectionNos, comp)
                                                                }
                                                            />
                                                            <ListItemText
                                                                primary={`${advisor.firstName} ${advisor.lastName} - ${advisor.title || advisor.designation || advisor.job || ''}`}
                                                            />
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </label>
                                        </div>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {sectionsByModule.map(({ module, moduleSections }, mIdx) => (
                            <React.Fragment key={module._id}>

                                {/* ── Module accordion header row ──────────────────────────────────── */}
                                {/* Each company column gets an explicit empty <td> so the browser   */}
                                {/* never recalculates column widths when sections are shown/hidden.  */}
                                <tr
                                    onClick={() => toggleAccordion(module._id)}
                                    style={{ background: '#f8f9fa', cursor: 'pointer' }}
                                >
                                    <td
                                        style={{
                                            padding: '10px 12px',
                                            paddingTop: mIdx > 0 ? '20px' : '10px',
                                            borderTop: mIdx > 0 ? '1px solid #DBDFEE' : 'none',
                                            width: '100% !important',
                                            minWidth: '100%!important',
                                        }}
                                    >
                                        <div className="df aic jcsb w100">
                                            <div className="df aic" style={{ gap: '8px' }}>
                                                <span className="fw600 c15 fs1rem">{module.moduleName}</span>
                                                {module.moduleKey && (
                                                    <span
                                                        className="fs0-75rem c15 px0-5rem py0-25rem br5px"
                                                        style={{ background: '#e9ecef', opacity: 0.8 }}
                                                    >
                                                        {module.moduleKey}
                                                    </span>
                                                )}
                                                <span className="fs0-75rem" style={{ color: '#888' }}>
                                                    ({moduleSections.length} section{moduleSections.length !== 1 ? 's' : ''})
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    {/* One empty td per company column — keeps layout stable */}
                                    {allCompanies.map((_, i) => (
                                        <td
                                            key={i}
                                            style={{
                                                background: '#f8f9fa',
                                                borderTop: mIdx > 0 ? '1px solid #DBDFEE' : 'none',
                                            }}
                                        />
                                    ))}
                                </tr>

                                {/* ── Section rows (rendered only when accordion is open) ──────────── */}
                                {openAccordions[module._id] && moduleSections.map((section, sIdx) => (
                                    <tr key={section.sectionNo}>
                                        {/* Section label — same style as PermissionsClient */}
                                        <td className="c15 bg0" style={{ background: '#fafbfe' }}>
                                            <span className="fw600 bg0" style={{ marginRight: '4px' }}>
                                                {section.sectionNo}
                                            </span>
                                            {section.sectionName}
                                        </td>

                                        {/* Per-company cell — avatars + Select dropdown (same as PermissionsClient) */}
                                        {allCompanies.map((comp, cIdx) => {
                                            // For main modules (e.g. Investment Readiness), only the company
                                            // that owns this module instance should have an active dropdown.
                                            // module.companies is a map of { [companyId]: true } set on the Module doc.
                                            const isCompanyLocked = module.isMainModule && !module.companies?.[comp._id];
                                            return (
                                                <td
                                                    className="c15 cp"
                                                    key={`${cIdx}-check-${section.sectionNo}-${sIdx}`}
                                                    style={isCompanyLocked ? { opacity: 0.4, pointerEvents: 'none' } : {}}
                                                >
                                                    <div
                                                        className="w100 df jcsb bg1 p0-5rem br30px"
                                                        style={{ border: '1px solid #DBDFEE' }}
                                                    >
                                                        {/* Circular avatars of advisors who have this permission */}
                                                        <div className="df">
                                                            {!isCompanyLocked && section.permission?.[comp._id] &&
                                                                Object.values(section.permission[comp._id]).map((advisor, keyIndex) => {
                                                                    return (
                                                                        advisor?.profile ? (
                                                                            <img
                                                                                key={`${keyIndex}-av`}
                                                                                src={advisor.profile}
                                                                                className={`w2rem h2rem df jcc aic br100 mr0-5rem pr ${profileClass}`}
                                                                                data-tooltip={advisor.firstName
                                                                                    ? `${advisor.firstName} ${advisor.lastName}`
                                                                                    : 'Register to show your name here'}
                                                                                alt=""
                                                                            />
                                                                        ) : (
                                                                            <span
                                                                                key={`${keyIndex}-av-init`}
                                                                                data-tooltip={advisor.firstName
                                                                                    ? `${advisor.firstName} ${advisor.lastName}`
                                                                                    : 'Register to show your name here'}
                                                                                className={`w2rem h2rem df jcc aic br100 mr0-5rem pr ${profileClass}`}
                                                                            >
                                                                                {advisor.firstName
                                                                                    ? `${advisor.firstName[0]}${advisor.lastName[0]}`
                                                                                    : '?'}
                                                                            </span>
                                                                        )
                                                                    )
                                                                })
                                                            }
                                                        </div>

                                                        {/* Per-advisor Select — lists all advisors with checkboxes */}
                                                        <div className="pr">
                                                            <Select
                                                                multiple
                                                                className="select-advisor w10"
                                                                id={`section-select-${comp._id}-${section.sectionNo}`}
                                                                sx={{ "& .MuiSvgIcon-root": { color: "#4A67FF" } }}
                                                                IconComponent={ExpandMoreIcon}
                                                                value={[]}
                                                                disabled={isCompanyLocked}
                                                                MenuProps={{
                                                                    PaperProps: {
                                                                        style: { maxHeight: 300, width: 'auto', minWidth: '200px' },
                                                                    },
                                                                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                                                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                                                                    getContentAnchorEl: null,
                                                                    keepMounted: true,
                                                                    marginThreshold: 10,
                                                                    style: { WebkitTransform: 'none', position: 'fixed' },
                                                                }}
                                                            >
                                                                {advisors.map((advisor, adIdx) => (
                                                                    <MenuItem
                                                                        key={`${section.sectionNo}-${comp._id}-${adIdx}`}
                                                                        value={advisor._id}
                                                                        className="select-advisor-list ttc"
                                                                    >
                                                                        <Checkbox
                                                                            checked={!!section.permission?.[comp._id]?.[advisor._id]}
                                                                            onChange={(e) => handlePermissions(e, advisor, section.sectionNo, comp)}
                                                                        />
                                                                        <ListItemText
                                                                            primary={`${advisor.firstName} ${advisor.lastName} - ${advisor.title || advisor.designation || advisor.job || ''}`}
                                                                        />
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </div>
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}

                            </React.Fragment>
                        ))}
                    </tbody>

                </table>
            </div>
        </div>
    );
}
